import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { authApi } from '../lib/api'
import type { AdminUser, LoginRequest } from '../types'

interface AuthContextType {
  user: AdminUser | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginRequest) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const initAuth = () => {
      const token = localStorage.getItem('admin_auth_token')
      const userData = localStorage.getItem('admin_user')
      
      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData) as AdminUser
          // Verify user is admin
          if (parsedUser.isAdmin) {
            setUser(parsedUser)
          } else {
            // Clear non-admin user data
            localStorage.removeItem('admin_auth_token')
            localStorage.removeItem('admin_user')
          }
        } catch (error) {
          console.error('Failed to parse user data:', error)
          localStorage.removeItem('admin_auth_token')
          localStorage.removeItem('admin_user')
        }
      }
      
      setIsLoading(false)
    }

    initAuth()
  }, [])

  const login = async (credentials: LoginRequest) => {
    try {
      const response = await authApi.login(credentials)
      
      // Verify user is admin
      if (!response.user.isAdmin) {
        throw new Error('Access denied. Admin privileges required.')
      }
      
      localStorage.setItem('admin_auth_token', response.token)
      localStorage.setItem('admin_user', JSON.stringify(response.user))
      setUser(response.user)
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  const logout = () => {
    authApi.logout()
    setUser(null)
  }

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}